import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { EMPTY, Subject, Subscription, timer } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { CustomIcons } from '@shared/models/custom-icons';
import { IDestroy, TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Store } from '@ngxs/store';
import { cloneDeep } from 'lodash';
import { SnackBarService } from '@shared/components/snackbar-message/services/snackbar.service';
import { MirSnackbarType } from '@shared/components/snackbar-message/model/snackbar';
import { DynamicCatalogFilesComponent } from '@shared/components/dynamic-catalog-files/dynamic-catalog-files/dynamic-catalog-files.component';
import { saveAs } from 'file-saver';
import { ChangedSelectCatalog } from '@shared/state/catalog/shared-catalog.action';
import { CatalogView } from '@shared/models/shared-catalog.state';
import { DOCUMENT } from '@angular/common';
import { SharedCatalogState } from '@shared/state/catalog/shared-catalog.state';
import { StatsMosService } from '@shared/services/stats-mos.service';
import { DownloadCatalogService } from '../../../../services/download-catalog.service';
import { DownloadDialogComponent } from './download-modal-dialog/download-dialog.component';
import { FileType, TypeInfo } from '../../../../models/catalog';
import { CatalogState } from '../../../../store/catalog/catalog.state';
import { CreateCatalogsFile, GetCatalogsExportStatus } from '../../../../store/catalog/catalog.action';
import { CatalogStateType } from '../../../../modules/catalog-items-data/models/catalog-item.service';
import { CatalogItemService } from '../../../../modules/catalog-items-data/services/catalog-item.service';

@Component({
  selector: 'app-download-button',
  templateUrl: './download-button.component.html',
  styleUrls: ['./download-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class DownloadButtonComponent implements AfterViewInit, IDestroy {
  private _timer: Subscription | null;
  public componentDestroy: () => Subject<any>;
  public icons = CustomIcons;
  public screenRes = 0;
  public menuOpen = false;
  public filesTypes: TypeInfo[] = [
    {
      idFile: 0,
      archiveFileSize: 0,
      format: FileType.JSON,
      status: null
    },
    {
      idFile: 0,
      archiveFileSize: 0,
      format: FileType.XLSX,
      status: null
    },
    {
      idFile: 0,
      archiveFileSize: 0,
      format: FileType.XML,
      status: null
    },
    {
      idFile: 0,
      archiveFileSize: 0,
      format: FileType.CSV,
      status: null
    }
  ];

  @ViewChild('menuTrigger') public menuTrigger: MatMenuTrigger;
  @Output() public readonly changedMenuOpen = new EventEmitter<boolean>();
  @Input() public buttonClass: 'white' | 'black';
  @Input() public isDynamic: boolean;
  @Input() public catalog: CatalogView;
  @Input() public catalogId: number;
  @Input() public releaseDate: string;
  @Input() public darkStyle = false;
  @Input() public disable = false;
  @Input() public isImpairedVersion = false;
  public breakPointObserve$ = this.breakpointObserver.observe(['(max-width: 1024px)']).pipe(
    tap(state => {
      if (state.breakpoints['(max-width: 1024px)']) {
        this.screenRes = 1;
        return;
      }
      this.screenRes = 0;
    })
  );
  public mobileDialog: MatDialogRef<DownloadDialogComponent, any>;

  constructor(
    private cdr: ChangeDetectorRef,
    private downloadService: DownloadCatalogService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private store: Store,
    private catalogItemService: CatalogItemService,
    private snackBar: SnackBarService,
    private r2: Renderer2,
    private statsMosService: StatsMosService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  private firstOpen(): void {
    if (this.catalog) {
      this.store.dispatch(new ChangedSelectCatalog(this.catalog));
    }
    this.store.dispatch(new GetCatalogsExportStatus([this.catalogId], this.releaseDate));
    const filesInfo = this.store.selectSnapshot(CatalogState.catalogFileInfo).call(this, this.catalogId)?.files;
    if (filesInfo) {
      if (!filesInfo.some(file => file.status === 'PROCESSING')) {
        filesInfo.forEach(file => {
          const currentFileIndex = this.filesTypes.findIndex(currentFile => currentFile.format === file.format);
          if (currentFileIndex !== -1) {
            this.filesTypes[currentFileIndex] = cloneDeep(file);
          }
        });
      }
    }
  }

  private download(content: Blob, fileName: string, contentType: string): void {
    const file = new Blob([content], { type: contentType });
    saveAs(file, fileName);
  }

  private getStatusTimer(time: number): void {
    if (!this._timer) {
      this._timer = timer(time)
        .pipe(
          tap(() => {
            this.store.dispatch(new GetCatalogsExportStatus([this.catalogId], this.releaseDate));
          })
        )
        .subscribe(() => {
          if (this._timer) {
            this._timer.unsubscribe();
            this._timer = null;
          }
        });
    }
  }

  public ngAfterViewInit(): void {
    this.store
      .select(CatalogState.catalogFileInfo)
      .pipe(
        map(filterFn => filterFn(this.catalogId)),
        takeUntil(this.componentDestroy())
      )
      .subscribe(res => {
        if (this.filesTypes && res?.files && res.files.length > 0) {
          this.filesTypes.forEach(file => {
            const fundedFile = res.files.find(f => f.format === file.format && f.status !== file.status);
            if (fundedFile) {
              file.status = fundedFile.status;
              file.idFile = fundedFile.idFile;
              file.archiveFileSize = fundedFile.archiveFileSize;
            }
          });
          if (this.filesTypes.some(x => x.status === 'PROCESSING')) {
            this.getStatusTimer(60000);
          }

          if (this.mobileDialog) {
            this.mobileDialog.componentInstance.filesTypes = this.filesTypes;
            this.mobileDialog.componentInstance.update();
          }
          this.cdr.detectChanges();
        }
      });
    this.menuTrigger?.menuOpened
      .pipe(
        tap(() => {
          this.firstOpen();
          if (this.isImpairedVersion) {
            this.r2.addClass(this.document.body, 'no-scroll-impaired');
          }
          this.menuOpen = true;
          this.changedMenuOpen.emit(this.menuOpen);
        }),
        takeUntil(this.componentDestroy())
      )
      .subscribe();
    this.menuTrigger?.menuClosed
      .pipe(
        tap(() => {
          this.menuOpen = false;
          if (this.isImpairedVersion) {
            this.r2.removeClass(this.document.body, 'no-scroll-impaired');
          }
          this.changedMenuOpen.emit(this.menuOpen);
          this.cdr.detectChanges();
        }),
        takeUntil(this.componentDestroy())
      )
      .subscribe();
  }

  public downloadFile($event: MouseEvent, fileInfo: TypeInfo): void {
    if (!fileInfo.idFile) {
      this.createFile($event, fileInfo);
    }

    if (fileInfo.idFile && (fileInfo.status === 'SUCCESS' || fileInfo.status === 'ARCHIVE')) {
      const catalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
      this.statsMosService
        .fireEvent({ action: 'setdownload', fileType: fileInfo.format, idDataset: catalog?.datasetId })
        .pipe(takeUntil(this.componentDestroy()))
        .subscribe({
          error: err => {
            if (err.status !== 200) {
              throw err;
            }
          }
        });
      this.downloadService
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        .downloadFile(fileInfo.idFile)
        .pipe(
          catchError(() => {
            this.snackBar.open(MirSnackbarType.Error, [
              {
                translate: true,
                message: 'SNACK_BAR_MESSAGE.EXPORT_ERROR'
              }
            ]);
            return EMPTY;
          }),
          tap((file: ArrayBuffer) => {
            const blob = new Blob([file], {
              type: 'application/zip'
            });
            saveAs(blob, `${catalog.datasetId + fileInfo.format}.zip`);
          }),
          switchMap(() =>
            this.catalogItemService.saveCatalogStatistic({
              idOpenCatalog: this.catalogId,
              type: CatalogStateType.DownloadCatalog
            })
          ),
          take(1),
          takeUntil(this.componentDestroy())
        )
        .subscribe();
    }
  }

  public openModal(): void {
    this.firstOpen();
    const config =
      this.screenRes === 2
        ? {
            maxWidth: '100%',
            width: '100%',
            position: {
              bottom: '0px'
            },
            panelClass: 'mobile-bottom-show-up-small-dialog',
            backdropClass: 'backdrop',
            hasBackdrop: true
          }
        : {
            panelClass: 'mat-dialog-responsive'
          };
    this.mobileDialog = this.dialog.open(DownloadDialogComponent, config);
    this.mobileDialog.componentInstance.isDynamic = this.isDynamic;
    this.mobileDialog.componentInstance.catalogId = this.catalogId.toString();
    this.mobileDialog.componentInstance.filesTypes = this.filesTypes;
    this.mobileDialog.componentInstance.downloadFile = this.downloadFile.bind(this);
    this.mobileDialog.componentInstance.createFile = this.createFile.bind(this);
    this.mobileDialog.componentInstance.downloadPasport = this.downloadPasport.bind(this);
    this.mobileDialog.componentInstance.openHistoricalDataModal = this.openHistoricalDataModal.bind(this);
  }

  public createFile($event: MouseEvent, file: TypeInfo): void {
    $event.stopPropagation();
    if (!file.idFile && !file.status) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.store
        .dispatch(new CreateCatalogsFile(this.catalogId, file.format, this.releaseDate))
        .pipe(take(1), takeUntil(this.componentDestroy()))
        .subscribe();
      this.getStatusTimer(10000);
    }
  }

  public downloadPasport(type: 'json' | 'csv'): void {
    let fileType;
    if (type === 'csv') {
      fileType = 'CSV';
    } else if (type === 'json') {
      fileType = 'JSON';
    }
    const catalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
    this.statsMosService
      .fireEvent({ action: 'passdownload', fileType: fileType, idDataset: catalog.datasetId })
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        error: err => {
          if (err.status !== 200) {
            throw err;
          }
        }
      });
    this.downloadService
      .downloadPassport(type, this.catalogId)
      .pipe(
        map(async res => {
          const file = new Blob([res], { type: 'application/json' });
          const fileText = await file.text();
          let hasError = false;
          if (fileText) {
            try {
              const jsonRes = JSON.parse(fileText);
              if (jsonRes.detail_message) {
                this.snackBar.open(MirSnackbarType.Error, [
                  {
                    translate: true,
                    message: 'SNACK_BAR_MESSAGE.PASSPORT_ERROR'
                  }
                ]);
                return res;
              }
            } catch (objError) {
              this.download(res, `meta_${catalog.datasetId}.${type}`, `application/${type}`);
              hasError = true;
            }
          }
          if (!hasError) {
            this.download(res, `meta_${catalog.datasetId}.${type}`, `application/${type}`);
          }
          return res;
        }),
        take(1)
      )
      .subscribe();
  }

  public openHistoricalDataModal(): void {
    if (this.catalogId) {
      const dynamicCatalogFilesComponent = this.dialog.open(DynamicCatalogFilesComponent);
      dynamicCatalogFilesComponent.componentInstance.catalogId = this.catalogId;
    }
  }
  public ngOnDestroy(): void {
    if (this._timer) {
      this._timer.unsubscribe();
    }
  }

  public buttonDownloadClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }
}
